/*
 * 法务三页（行为准则 / 服务条款 / 隐私政策）的正文，按品牌各写一份：每个站点的措辞、章节结构都不一样，
 * 但事实（主体公司、收集哪些数据、退款口径、适用印尼法律）保持一致。
 *
 * 每个品牌文件导出 { conduct, tos, privacy }，每份文档：
 *   {
 *     title: 'Terms of Service',
 *     updated: '15 October 2026',
 *     lead: ['开头段落', ...],
 *     sections: [
 *       { heading: '1. Who we are', body: ['段落', { list: ['条目', ...] }, { note: '提示框' }] },
 *     ],
 *   }
 * 文案里可以写 {{company}} {{address}} {{email}} {{phone}} {{domain}}，渲染时用 runtime-env.js 的品牌配置替换，
 * 换公司 / 邮箱不用改文案。没有单独文案的品牌（triklet、infnogames 等）用 default。
 */
import env from '@/assets/js/env'

const LOADERS = {
  seavoucher: () => import(/* webpackChunkName: "legal-seavoucher" */ './seavoucher'),
  pasarkartu: () => import(/* webpackChunkName: "legal-pasarkartu" */ './pasarkartu'),
  cpvoucher: () => import(/* webpackChunkName: "legal-cpvoucher" */ './cpvoucher'),
  kukukard: () => import(/* webpackChunkName: "legal-kukukard" */ './kukukard'),
  sakutop: () => import(/* webpackChunkName: "legal-sakutop" */ './sakutop'),
  default: () => import(/* webpackChunkName: "legal-default" */ './default'),
}

export const DOC_KEYS = ['conduct', 'tos', 'privacy']

// 三份文档在各站点的叫法（页脚链接和正文标题都用这里，保证一致）；线上 5 个站点互不相同
const TITLES = {
  seavoucher: { conduct: 'Acceptable Use Policy', tos: 'Terms of Use', privacy: 'Privacy Statement' },
  pasarkartu: { conduct: 'Community Guidelines', tos: 'Terms of Service', privacy: 'Privacy Policy' },
  cpvoucher: { conduct: 'Code of Conduct', tos: 'Terms and Conditions', privacy: 'Privacy Notice' },
  kukukard: { conduct: 'House Rules', tos: 'Kukukard Terms', privacy: 'Privacy Guide' },
  sakutop: { conduct: 'Fair Play Policy', tos: 'Service Terms', privacy: 'Data Privacy Policy' },
  default: { conduct: 'Rules of Conduct', tos: 'Terms of Service', privacy: 'Privacy Policy' },
}

export function legalTitles() {
  return TITLES[env.brand] || TITLES.default
}

const tokens = () => ({
  company: env.companyName || env.brandName || '',
  address: env.address || '',
  email: env.email || '',
  phone: env.phone || '',
  domain: (env.domains && env.domains[0]) || window.location.hostname,
})

const fill = (text, t) => String(text).replace(/\{\{(\w+)\}\}/g, (m, k) => (k in t ? t[k] : m))

const fillBlock = (block, t) => {
  if (typeof block === 'string') return fill(block, t)
  if (block.list) return { list: block.list.map(i => fill(i, t)) }
  if (block.note) return { note: fill(block.note, t) }
  return block
}

const fillDoc = (doc, t) => ({
  title: fill(doc.title, t),
  updated: doc.updated || '',
  lead: (doc.lead || []).map(p => fill(p, t)),
  sections: (doc.sections || []).map((s, i) => ({
    id: 'sec-' + (i + 1),
    heading: fill(s.heading, t),
    body: (s.body || []).map(b => fillBlock(b, t)),
  })),
})

let cache = null

export function loadLegal() {
  if (!cache) {
    const loader = LOADERS[env.brand] || LOADERS.default
    cache = loader().then((mod) => {
      const data = mod.default || mod
      const t = tokens()
      const out = {}
      const titles = legalTitles()
      DOC_KEYS.forEach((k) => { out[k] = Object.assign(fillDoc(data[k], t), { title: titles[k] }) })
      return out
    })
  }
  return cache
}
